/**
 * Hook for reading/writing a single facet on an entity.
 * Used for description (common.description) — one facet per entity.
 */

import { useCallback, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";

interface FacetItem {
  readonly id: string;
  readonly entity_id: string;
  readonly schema_id: string;
  readonly data: Record<string, unknown>;
  readonly created_at: string;
}

interface FacetListResponse {
  readonly items: readonly FacetItem[];
  readonly total: number;
}

interface UseEntityFacetResult {
  readonly data: Record<string, unknown> | null;
  readonly facetId: string | null;
  readonly isLoading: boolean;
  readonly save: (data: Record<string, unknown>) => void;
}

const SAVE_DEBOUNCE_MS = 800;

export function useEntityFacet(
  entityId: string | undefined,
  schemaId: string,
): UseEntityFacetResult {
  const runtime = useAppRuntime();
  const queryClient = useQueryClient();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryKey = ["facet", entityId, schemaId];

  const { data: response, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      runtime.transport.rpc<FacetListResponse>("graph.facet.list", {
        entity_id: entityId,
        schema_id: schemaId,
      }),
    enabled: !!entityId,
  });

  const firstFacet = response?.items[0] ?? null;

  const save = useCallback(
    (newData: Record<string, unknown>) => {
      if (!entityId) return;

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      saveTimerRef.current = setTimeout(async () => {
        try {
          await runtime.transport.rpc("graph.facet.attach", {
            entity_id: entityId,
            schema_id: schemaId,
            version: 1,
            data: newData,
            source: "user",
            // Use entity_id as external_id to ensure upsert (one description per entity)
            external_id: `${entityId}:${schemaId}`,
          });
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          queryClient.invalidateQueries({ queryKey });
        } catch (err) {
          console.error("Failed to save facet:", err);
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [entityId, schemaId, runtime.transport, queryClient, queryKey],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return {
    data: firstFacet ? (firstFacet.data) : null,
    facetId: firstFacet?.id ?? null,
    isLoading,
    save,
  };
}
